import { useEffect, useState, useCallback } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import CandidateCard from "@/components/CandidateCard";
import { Skeleton } from "@/components/ui/skeleton";

const REGIONS = ["All", "North", "West", "Central", "East", "South"];
const ADVOCACIES = ["All", "Education", "Climate", "Healthcare", "Youth Empowerment", "Technology", "Women's Leadership"];

export default function Candidates() {
  const [items, setItems] = useState(null);
  const [region, setRegion] = useState("All");
  const [advocacy, setAdvocacy] = useState("All");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("votes");

  const load = useCallback(() => {
    setItems(null);
    api.get("/candidates", { params: { region, advocacy, q: q || undefined, sort, limit: 100 } })
      .then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [region, advocacy, q, sort]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  return (
    <div className="section py-12 sm:py-16" data-testid="candidates-page">
      <p className="kicker mb-2">54 Delegations · One Crown</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50">Candidate Directory</h1>
      <p className="mt-3 max-w-2xl text-amber-100/60">Meet the women representing the continent. Filter by region, advocacy or search by name.</p>

      <div className="mt-8 flex flex-col gap-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gold/50" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or country..."
            data-testid="candidate-search"
            className="w-full rounded-full border border-gold/25 bg-obsidian-700 py-3 pl-10 pr-4 text-sm text-amber-50 outline-none focus:border-gold"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-gold/60" />
          {REGIONS.map((r) => (
            <button key={r} onClick={() => setRegion(r)} data-testid={`filter-region-${r.toLowerCase()}`}
              className={`rounded-full px-3 py-1.5 text-xs font-display uppercase tracking-wider transition-all ${region === r ? "gold-bg text-obsidian" : "border border-gold/25 text-gold/70 hover:border-gold/60"}`}>
              {r}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {ADVOCACIES.map((a) => (
            <button key={a} onClick={() => setAdvocacy(a)} data-testid={`filter-advocacy-${a.replace(/\s+/g, '-').toLowerCase()}`}
              className={`rounded-full px-3 py-1.5 text-xs transition-all ${advocacy === a ? "border border-emerald-accent text-emerald-accent" : "border border-white/10 text-amber-50/50 hover:border-white/25"}`}>
              {a}
            </button>
          ))}
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="candidate-sort"
            className="ml-auto rounded-full border border-gold/25 bg-obsidian-700 px-3 py-1.5 text-xs text-amber-50 outline-none">
            <option value="votes">Most Votes</option>
            <option value="trending">Trending</option>
            <option value="name">A–Z</option>
          </select>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="candidate-grid">
        {items === null
          ? Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] w-full rounded-lg bg-obsidian-600" />)
          : items.length === 0
          ? <p className="col-span-full py-20 text-center text-muted-foreground">No candidates match your filters.</p>
          : items.map((c, i) => <CandidateCard key={c.id} candidate={c} rank={sort === "votes" ? i + 1 : undefined} index={i} />)}
      </div>
    </div>
  );
}
